import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Predictive search behavior for both the standard modal structure and the desktop
 * header structure, where the result container is inside a nested dialog-component.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Track whether the last interaction was keyboard-based.
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * Get the standard modal dialog component.
   * @returns {DialogComponent | null}
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get #isDesktopSearch() {
    return this.hasAttribute('data-desktop-search');
  }

  get #desktopDialog() {
    const dialog = this.querySelector('[data-desktop-search-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #predictiveSearchResults() {
    const results = this.querySelector('[ref="predictiveSearchResults"]');
    return results instanceof HTMLElement ? results : null;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });

    if (this.#isDesktopSearch) {
      this.refs.searchInput.addEventListener('focus', this.#handleDesktopInputFocus, { signal });
      this.refs.searchInput.addEventListener('click', this.#handleDesktopInputFocus, { signal });
      this.#desktopDialog?.addEventListener('cancel', this.#handleDesktopCancel, { signal });
      document.addEventListener('click', this.#handleDocumentClick, { signal });
      this.#predictiveSearchResults?.addEventListener('click', this.#handleResultsClick, { signal });
    } else if (this.dialog) {
      this.dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input.
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  #handleDesktopInputFocus = () => {
    this.#openDesktopDialog();

    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    const dialog = this.#desktopDialog;
    const target = event.target;

    if (!dialog?.open || !(target instanceof Node) || this.contains(target)) return;

    this.#closeDesktopDialog();
  };

  /**
   * @param {Event} event
   */
  #handleDesktopCancel = (event) => {
    event.preventDefault();
    this.#closeDesktopDialog();
  };

  closeDesktopDialog = () => {
    this.#closeDesktopDialog();
    this.#resetSearch();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleResultsClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;
    if (!target.closest('.predictive-search-results__clear')) return;

    this.clearRecentlyViewedProducts(event);
  };

  #openDesktopDialog() {
    const dialog = this.#desktopDialog;

    if (!this.#isDesktopSearch || !dialog || dialog.open) return;

    dialog.show();
  }

  #closeDesktopDialog() {
    const dialog = this.#desktopDialog;

    if (!this.#isDesktopSearch || !dialog?.open) return;

    this.refs.searchInput.blur();
    dialog.close();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (!event.metaKey || event.key !== 'k' || !this.#shouldHandleShortcut()) return;

    event.preventDefault();

    if (this.#isDesktopSearch) {
      this.refs.searchInput.focus();
      this.#openDesktopDialog();

      if (!this.#emptyStateLoaded) {
        this.#loadEmptyState();
      }

      return;
    }

    this.dialog?.toggleDialog();
  };

  #shouldHandleShortcut() {
    const desktopMediaQuery = window.matchMedia('(min-width: 750px)');

    return this.#isDesktopSearch ? desktopMediaQuery.matches : !desktopMediaQuery.matches;
  }

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    if (this.refs.searchInput.value.trim().length) return;

    this.#emptyStateLoaded = true;
    this.#resetSearch();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }

        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      this.#closeDesktopDialog();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1
          : currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        this.#handleSearchEnter(event);
        break;
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleSearchEnter(event) {
    const results = this.#predictiveSearchResults;
    const singleResultContainer = results?.querySelector('[data-single-result-url]');

    if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
      event.preventDefault();
      window.location.href = singleResultContainer.dataset.singleResultUrl;
      return;
    }

    if (this.#currentIndex >= 0) {
      event.preventDefault();
      this.#currentItem?.querySelector('a')?.click();
      return;
    }

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', this.refs.searchInput.value);
    window.location.href = searchUrl.toString();
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const recentlyViewedItems =
      this.refs.recentlyViewedItems || Array.from(this.querySelectorAll('[ref="recentlyViewedItems[]"]'));
    const recentlyViewedTitle =
      this.refs.recentlyViewedTitle || Array.from(this.querySelectorAll('[ref="recentlyViewedTitle[]"]'));
    const recentlyViewedWrapper =
      this.refs.recentlyViewedWrapper || this.querySelector('[ref="recentlyViewedWrapper"]');

    const allRecentlyViewedElements = [...recentlyViewedItems, ...recentlyViewedTitle];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper instanceof HTMLElement) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * @param {InputEvent} event
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#openDesktopDialog();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const results = this.#predictiveSearchResults;

      results?.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    const results = this.#predictiveSearchResults;

    if (!this.dataset.sectionId || !results) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        morph(results, resultsMarkup);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>}
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const results = this.#predictiveSearchResults;
    const { searchInput } = this.refs;
    const emptySectionId = 'custom-predictive-search-empty';

    if (!results) return;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          const resultsInner = parsedEmptySectionMarkup.querySelector('.predictive-search-results__inner');

          if (collectionElement) {
            collectionElement.replaceWith(recentlyViewedProductsHtml);
          } else {
            resultsInner?.replaceChildren(recentlyViewedProductsHtml);
          }
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(results, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const closeButton = target.closest('[data-desktop-search-close]');
  const search = closeButton?.closest('[data-desktop-search]');
  const dialog = search?.querySelector('[data-desktop-search-dialog]');
  const input = search?.querySelector('[ref="searchInput"]');

  if (!(dialog instanceof HTMLDialogElement)) return;

  if (input instanceof HTMLElement) input.blur();
  dialog.close();
});
